/**
 * Where a task's master solution / grading scheme came from.
 *
 * Recorded PER TASK, because a single exam can hold both: the teacher uploads a solution that covers most
 * tasks, and the generation fills the remaining ones without touching the uploaded parts. Nothing
 * exam-level can express that — `exam.masterSolutionIds` says a file was uploaded, not which tasks it
 * covered, so only this per-task value answers "was this task's solution written by the AI?".
 *
 * Editing existing content in the canvas deliberately does NOT change its source: where it originally came
 * from does not change because the teacher adjusted it, and `isApproved` already records their sign-off.
 * Writing content for a task that had none is different — that is authorship, and it records 'manual'.
 */
export type SolutionSource =
  /** Extracted from a document the teacher uploaded. The only kind that carries OCR confidence. */
  | 'upload'
  /** Written by the generation agent (including a refine, which replaces what was there). */
  | 'generated'
  /** Typed by the teacher in the canvas, for a task that had nothing yet. Neither AI-authored nor extracted. */
  | 'manual';

/**
 * An exam's provenance for one section, derived from its tasks — what the status bar and the agent-facing
 * status texts show. `mixed` is the case the per-task field exists for.
 */
export type SolutionSourceState = SolutionSource | 'mixed';

export const SOLUTION_SOURCES: readonly SolutionSource[] = ['upload', 'generated', 'manual'] as const;
